import { Box, Button, ButtonGroup, Divider, Grid, Icon, IconButton, ListItem, makeStyles, MenuItem, Typography, useTheme } from "@mui/material";
import useReviewer from "../hooks/useReviewer";
import Select from '@mui/material/Select';
import DOMPurify from "dompurify";
import { ReactNode } from "react";
import FallbackSpinner from "../../../../materio/@core/components/spinner";
import PageHeader from "../../../../materio/@core/components/page-header";

type Props = {
    text: string,
    contentType: 'SalesPitch',
    onClose: () => void,
    onAccept: (updatedText: string) => void
}

function createMarkup(html: string) {
    return {
        __html: DOMPurify.sanitize(html)
    }
}

const AIReviewer = ({ text, onClose, onAccept, contentType }: Props) => {
    const { result, toggleSwapped, isReviewing } = useReviewer(text, contentType);
    const theme = useTheme();

    const renderText = () => {
        var nodes = [] as ReactNode[];

        for (var i = 0; i < result.length; i++) {
            const section = result[i];
            const color = section.swapped ? 'success' : 'error';
            const sectionText = section.swapped ? section.suggested : section.original;
            const titleText = section.swapped ? `Original text: ${section.original}` : `Suggested change: ${section.suggested}\n\nReason: ${section.reason}`;

            nodes.push((
                <>
                    <Grid container sx={{ mb: 2 }} rowGap={1}>
                        <Grid item xs={1} alignItems='center' display='flex' >
                            Original
                        </Grid>
                        <Grid item xs={9} sx={{}}>
                            <Typography>
                                <span style={{ display: 'inline-block', textDecoration: section.swapped ? 'line-through' : undefined, paddingLeft: 5, color: theme.palette.error.contrastText }} dangerouslySetInnerHTML={createMarkup(section.original)}></span>
                            </Typography>
                        </Grid>
                        <Grid xs={2}>

                        </Grid>
                        <Grid item xs={1} alignItems='center' display='flex'>
                            Suggested
                        </Grid>
                        <Grid item xs={9} sx={{}}>
                            <Typography>
                                <span style={{ display: 'inline-block', paddingLeft: 5 }} dangerouslySetInnerHTML={createMarkup(section.suggested)}></span>
                            </Typography>
                        </Grid>
                        <Grid xs={2} sx={{ textAlign: 'center' }}>
                        </Grid>
                        <Grid item xs={1} alignItems='center' display='flex'>
                            <Typography variant="caption">
                                <span style={{ display: 'inline-block', paddingLeft: 5, fontStyle: 'italic' }}>Reason</span>
                            </Typography>
                        </Grid>
                        <Grid item xs={9} sx={{}}>
                            <Typography variant="caption">
                                <span style={{ display: 'inline-block', paddingLeft: 5, fontStyle: 'italic' }} dangerouslySetInnerHTML={createMarkup(section.reason)}></span>
                            </Typography>
                        </Grid>
                        <Grid xs={2}>
                            <Button color={color} onClick={() => toggleSwapped(section)} size="small" endIcon={<Icon>swap_horiz_outlined</Icon>} variant="contained">{section.swapped ? "Accepted" : "Rejected"}
                            </Button>
                        </Grid>
                    </Grid>
                    <Divider />
                </>
            ))
        }

        return nodes;
    }

    const handleAccept = () => {
        let updatedText = '';

        for (var i = 0; i < result.length; i++) {
            const section = result[i];

            const sectionText = section.swapped ? section.suggested : section.original;
            
            updatedText += `${sectionText}`;
        }

        onAccept(updatedText)
        onClose();
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <PageHeader
                    title={<Typography variant='h5'>{isReviewing && <span>Reviewing text, please wait..</span>} {!isReviewing && <span>Smart Review</span>}</Typography>}
                    subtitle={
                        isReviewing && <Typography variant='body2'>
                            This may take a few minutes
                        </Typography>
                    }
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, rowGap: 2, flex: '0 0 auto', alignItems: 'center' }}>
                    {!isReviewing && <Button variant="contained" size="small" onClick={handleAccept}>Accept Changes</Button>}
                    <Button variant="contained" size="small" onClick={onClose}>Cancel</Button>
                </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ minHeight: 50, flex: 1, overflow: 'auto' }}>
                    {!isReviewing && renderText()}
                    {isReviewing && <FallbackSpinner sx={{ height: '100%' }} />}
                </Box>
            </Box>
        </Box>
    )
}

export default AIReviewer;